<template>
  <EditorWrapper class="max-w-3xl mx-auto rounded-lg p-12 mb-8 fadeIn">
    <h1 class="mb-6 text-xl">
      {{ editMode ? "Update" : "Create New" }} Reward
    </h1>
    <form class="edyst-form-group" @submit.prevent="handleSubmit">
      <div class="flex justify-between items-center">
        <label for="name" class="label">
          Name
        </label>
        <div class="w-full">
          <input
            id="name"
            v-model="reward.name"
            v-validate="'required'"
            type="text"
            autocomplete="off"
            name="name"
            placeholder="Write Reward name here"
          />
          <small class="err-msg text-red block mt-1">
            {{ veeValidateErrors.first("name") }}
          </small>
        </div>
      </div>
      <div class="flex justify-between items-center">
        <label for="points" class="label">
          Redeem Points
        </label>
        <div class="w-full">
          <input
            id="points"
            v-model="reward.xp_points"
            v-validate="'required|numeric|min_value:1'"
            type="number"
            name="points"
            placeholder="Enter Number of XP Points Required to Redeem"
          />
          <small class="err-msg text-red block mt-1">
            {{ veeValidateErrors.first("points") }}
          </small>
        </div>
      </div>
      <div class="flex justify-start items-center">
        <label for="redemption" class="label">
          Max Redemption
        </label>
        <div class="w-full">
          <input
            id="redemption"
            v-model="reward.max_redemption_count"
            v-validate="'required|numeric|min_value:1'"
            type="number"
            name="redemption"
            placeholder="Enter Number of times a single user can redeem this reward"
          />
          <small class="err-msg text-red block mt-1">
            {{ veeValidateErrors.first("redemption") }}
          </small>
        </div>
      </div>
      <div class="flex justify-between items-center">
        <label for="stock" class="label">
          Stock
        </label>
        <div class="w-full">
          <input
            id="stock"
            v-model="reward.stock"
            v-validate="'required|numeric|min_value:1'"
            type="number"
            name="stock"
            placeholder="Enter Number of Stocks"
          />
          <small class="err-msg text-red block mt-1">
            {{ veeValidateErrors.first("stock") }}
          </small>
        </div>
      </div>
      <div class="flex justify-between items-start mt-3">
        <label class="label">
          Image
        </label>
        <div class="w-full">
          <div
            v-if="reward.image_url"
            class="text-center flex flex-col items-center"
            style="max-width: 20rem;min-height:10rem"
          >
            <img :src="reward.image_url" class="w-full h-full m-auto" />
            <BaseButton
              btnStyle="red-bordered"
              class="border border-red text-red px-2 py-1 rounded mt-1 flex justify-center items-center text-sm"
              @click.prevent="reward.image_url = ''"
            >
              <span class="material-icons text-lg mr-1">
                delete
              </span>
              Delete Image
            </BaseButton>
          </div>
          <div v-else class="w-full flex items-start">
            <div class="image-upload-widget w-full fadeIn">
              <S3FileUploader
                :serverUrl="serverUrl"
                :class="{ 'is-invalid': veeValidateErrors.first('image') }"
                :category="'reward-images'"
                class="text-center"
                @FILE_UPLOAD_SUCCESS="handleImageUpload"
              >
                <span class="material-icons text-5xl text-grey">
                  add_photo_alternate
                </span>
                <div class="text-base font-bold">Drop images here</div>
                <span class="text-sm">or click here to select</span>
              </S3FileUploader>
              <small class="err-msg text-red block mt-1">
                {{ veeValidateErrors.first("image") }}
              </small>
              <Tip class="text-grey-dark" />
            </div>
          </div>
          <input
            v-validate="'required'"
            :value="reward.image_url"
            type="hidden"
            name="image"
          />
        </div>
      </div>
      <div class="flex justify-between items-start mt-3">
        <label class="label">
          Description
        </label>
        <div class="w-full">
          <QuillEditor
            v-model="reward.description"
            :options="editorOptions"
            class="description-field"
            :class="{ 'is-invalid': veeValidateErrors.first('description') }"
          />
          <input
            v-validate="'required'"
            :value="reward.description"
            type="hidden"
            name="description"
          />
          <small class="err-msg text-red block mt-1">
            {{ veeValidateErrors.first("description") }}
          </small>
        </div>
      </div>
      <div class="flex justify-between items-center">
        <label for="typeform_url" class="label">
          Typeform Link
        </label>
        <div class="w-full">
          <input
            id="typeform_url"
            v-model="reward.typeform_url"
            v-validate="'required|url'"
            type="url"
            autocomplete="off"
            name="typeform url"
            placeholder="Enter typeform link"
          />
          <small class="err-msg text-red block mt-1">
            {{ veeValidateErrors.first("typeform url") }}
          </small>
        </div>
      </div>
      <div
        class="flex items-center mt-12 w-full"
        :class="editMode ? 'justify-between' : 'justify-end'"
      >
        <BaseButton
          v-if="editMode"
          class="inline-block px-12 py-2 mr-4"
          btnStyle="red-bordered"
          @click.prevent="deleteItem(reward)"
        >
          Delete Reward
        </BaseButton>
        <div class="flex items-center">
          <BaseButton
            class="inline-block px-12 py-2 mr-4"
            btnStyle="link"
            @click.prevent="cancel"
          >
            Cancel
          </BaseButton>
          <BaseButton class="inline-block px-12 py-2" btnStyle="blue">
            {{ editMode ? "Update" : "Create" }}
          </BaseButton>
        </div>
      </div>
    </form>
  </EditorWrapper>
</template>

<script>
import S3FileUploader from "@edyst/shared-components/src/components/S3FileUploader"
import siteConfig from "@/utils/siteConfig"
import {
  createReward,
  fetchRewardById,
  putReward,
  deleteReward,
} from "@/apis/rewards"
import EditorWrapper from "@/components/shared/EditorWrapper"
import formUtilMixin from "@/mixins/formUtilMixin"
import QuillEditor from "@/components/Quill/QuillEditor"
import BaseButton from "@/components/Base/BaseButton"
import Tip from "@/components/shared/Tip"

export default {
  components: {
    EditorWrapper,
    QuillEditor,
    BaseButton,
    S3FileUploader,
    Tip,
  },
  mixins: [formUtilMixin],
  props: {
    editMode: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      reward: {
        name: "",
        xp_points: "",
        max_redemption_count: "",
        stock: "",
        description: "",
        image_url: "",
        typeform_url: "",
      },
      serverUrl: `${siteConfig.siteURL}uploads/public`,
    }
  },
  computed: {
    editorOptions() {
      return {
        modules: {
          toolbar: [
            [{ header: [1, 2, 3, 4, false] }],
            ["bold", "italic", "underline", "strike"],
            [{ list: "ordered" }, { list: "bullet" }],
            [{ script: "sub" }, { script: "super" }],
            ["blockquote", "code-block"],
            [{ indent: "-1" }, { indent: "+1" }],
            ["image"],
            ["clean"],
          ],
        },
        placeholder: "Write Description Here...",
      }
    },
  },
  async created() {
    if (this.editMode) {
      try {
        const res = await fetchRewardById(this.$route.params.id)
        this.reward = res.data.item
        window.pageTitle = `Edit ${this.reward.name}`
      } catch (err) {
        this.$noty.error("Failed to get details of the reward")
      }
    }
  },
  methods: {
    async handleSubmit() {
      const result = await this.$validator.validateAll()
      if (!result) {
        this.$noty.error("Please fix the errors and try again")
        return
      }

      if (this.editMode) this.updateReward()
      else this.addReward()
    },
    updateReward() {
      const payload = {
        ...this.reward,
        stock: parseInt(this.reward.stock),
      }
      putReward(payload.id, payload)
        .then(() => {
          this.$noty.success(`Reward updated!`)
          setTimeout(() => {
            this.$router.push({ name: "RewardsList" })
          }, 1000)
        })
        .catch(err => {
          const msg =
            err.response && err.response.data
              ? this.$options.filters.capitalize(err.response.data.message)
              : "Failed to update the Reward. Please try again"
          this.$swal({
            icon: "warning",
            text: msg,
          })
        })
    },
    addReward() {
      const payload = {
        ...this.reward,
        stock: parseInt(this.reward.stock),
      }
      createReward(payload)
        .then(() => {
          this.$noty.success(`New Reward created: ${this.reward.name}`)
          setTimeout(() => {
            this.$router.push({ name: "RewardsList" })
          }, 1000)
        })
        .catch(() => {
          this.$noty.error("Failed to create the Reward. Please try again")
        })
    },
    async deleteItem({ id, name }) {
      if (
        !(await this.confirmDelete(`Are you sure you want to delete ${name}?`))
      )
        return
      deleteReward(id)
        .then(() => {
          this.$noty.success(`Successfully deleted ${name}`)
          this.$router.replace({ name: "RewardsList" })
        })
        .catch(err => {
          console.error(err)
          const msg =
            err.response && err.response.data
              ? this.$options.filters.capitalize(err.response.data.message)
              : `Failed to delete ${name}`
          this.$swal({
            text: msg,
            icon: "warning",
            buttons: "Close",
          })
        })
    },
    handleImageUpload(awsImageUrl) {
      if (awsImageUrl) {
        this.reward.image_url = awsImageUrl
        this.$noty.success("Image upload successful")
      }
    },
    async cancel() {
      if (this.isFormModified && !(await this.confirmExit())) return
      this.$router.go(-1)
    },
  },
}
</script>

<style scoped>
.label {
  display: inline-block;
  min-width: 10rem;
}

.description-field {
  overflow: auto;
  max-height: 60vh;
}
</style>
